import React from 'react'
import { cn } from '../../utils/cn'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'icon' | 'dark' | 'accent'
  size?: 'sm' | 'md' | 'lg'
  children?: React.ReactNode
  icon?: React.ReactNode
  loading?: boolean
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  icon,
  loading = false,
  className = '',
  disabled,
  ...props
}) => {
  const baseClasses =
    'inline-flex items-center justify-center gap-2 font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500 disabled:opacity-50 disabled:pointer-events-none rounded-full active:scale-[0.98]'

  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-2.5 text-sm',
    lg: 'px-8 py-3.5 text-base font-semibold',
  }

  const variantClasses = {
    primary:
      'bg-brand-700 text-white hover:bg-brand-800 shadow-lg shadow-brand-700/25 hover:shadow-brand-700/40 border border-transparent',
    secondary:
      'bg-white text-slate-900 hover:bg-slate-50 border border-slate-200 shadow-sm hover:border-brand-200',
    outline: 'bg-transparent border border-brand-200 text-brand-700 hover:bg-brand-50',
    ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900',
    icon: 'w-10 h-10 p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 focus-visible:ring-slate-500 rounded-full',
    dark: 'bg-brand-950 text-white hover:bg-brand-900 shadow-lg shadow-brand-950/20 border border-transparent',
    accent:
      'bg-brand-400 text-brand-950 hover:bg-brand-300 shadow-lg shadow-brand-400/20 border border-transparent font-bold',
  }

  // Handle icon variant specific sizing if needed, currently reusing sizeClasses for non-icon variants
  // For 'icon' variant, size is fixed in variantClasses, but we might want to allow sizing.
  // The original code had fixed size for icon variant.

  return (
    <button
      className={cn(
        baseClasses,
        variantClasses[variant],
        variant !== 'icon' && sizeClasses[size],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {!loading && icon}
      {variant !== 'icon' && children}
    </button>
  )
}
